import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { GridColDef } from "@mui/x-data-grid";
import ActionButton from "../../client-config/action-button";
import { EmailIngestion, SftpIngestion } from "../types";

interface ColumnHandlers<T> {
  onEdit: (row: T) => void;
  onDelete: (row: T) => void;
}

interface IngestionRow {
  id: number;
  description: string;
  enabled: boolean;
  settingEnabled: boolean;
}

// The Setting can be shared with other legacy rows, so it is only reported
// here, not toggled; the row's own flag is what the dialog edits.
const statusCell = (row: IngestionRow) => {
  if (!row.enabled) {
    return <Chip size="small" label="Disabled" />;
  }
  return row.settingEnabled ? (
    <Chip size="small" color="success" label="Enabled" />
  ) : (
    <Tooltip title="The import setting this ingestion belongs to is disabled, so the middleware skips it.">
      <Chip size="small" color="warning" label="Setting disabled" />
    </Tooltip>
  );
};

const statusColumn = <T extends IngestionRow>(): GridColDef<T> => ({
  field: "enabled",
  headerName: "Status",
  width: 140,
  renderCell: ({ row }) => statusCell(row),
});

const actionsColumn = <T extends IngestionRow>({
  onEdit,
  onDelete,
}: ColumnHandlers<T>): GridColDef<T> => ({
  field: "actions",
  type: "actions",
  width: 110,
  getActions: ({ row }) => [
    <Tooltip key="edit" title="Edit">
      <ActionButton
        aria-label={`Edit ${row.description}`}
        onClick={(event) => {
          event.stopPropagation();
          onEdit(row);
        }}
      >
        <EditIcon fontSize="small" />
      </ActionButton>
    </Tooltip>,
    <Tooltip key="delete" title="Delete">
      <ActionButton
        aria-label={`Delete ${row.description}`}
        onClick={(event) => {
          event.stopPropagation();
          onDelete(row);
        }}
      >
        <DeleteIcon fontSize="small" />
      </ActionButton>
    </Tooltip>,
  ],
});

export const getSftpColumns = (
  handlers: ColumnHandlers<SftpIngestion>,
): GridColDef<SftpIngestion>[] => [
  { field: "description", headerName: "Description", flex: 1.5 },
  { field: "sourceDirectory", headerName: "Source directory", flex: 1.5 },
  {
    field: "sftpServer",
    headerName: "SFTP server",
    flex: 1,
    valueGetter: (_value, row) =>
      row.sftpServer
        ? `${row.sftpServer.hostName}:${row.sftpServer.portNumber ?? ""}`
        : "Server missing",
  },
  {
    field: "startDirectory",
    headerName: "Start directory",
    flex: 1,
    valueGetter: (_value, row) => row.sftpServer?.startDirectory ?? "",
  },
  statusColumn<SftpIngestion>(),
  actionsColumn(handlers),
];

const destinationOf = (row: EmailIngestion): string => {
  if (row.destinationFileShare) {
    return row.destinationFileShare.path;
  }
  return row.destinationSftp ? `SFTP: ${row.destinationSftp.hostName}` : "None";
};

export const getEmailColumns = (
  handlers: ColumnHandlers<EmailIngestion>,
): GridColDef<EmailIngestion>[] => [
  { field: "description", headerName: "Description", flex: 1.5 },
  { field: "folder", headerName: "Mailbox folder", flex: 1 },
  { field: "senderMask", headerName: "Sender mask", flex: 1 },
  { field: "attachmentMask", headerName: "Attachment mask", flex: 1 },
  {
    field: "destination",
    headerName: "Destination",
    flex: 1.5,
    valueGetter: (_value, row) => destinationOf(row),
  },
  {
    field: "mailBox",
    headerName: "Mailbox",
    flex: 1,
    valueGetter: (_value, row) => row.mailBox?.description ?? "Mailbox missing",
  },
  statusColumn<EmailIngestion>(),
  actionsColumn(handlers),
];
